import { StatusChip } from '@batiflow/ui';
import {
  CheckmarkCircle16Filled,
  Clock16Regular,
  DismissCircle16Filled,
  Prohibited16Regular,
  Warning16Filled,
} from '@fluentui/react-icons';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DECISION_INTENT,
  FLOW_STATUS_INTENT,
  type ApprovalDecisionValue,
  type ApprovalFlowStatus,
} from './approvals-model';

const STATUS_ICON: Record<ApprovalFlowStatus, ReactElement> = {
  in_progress: <Clock16Regular />,
  approved: <CheckmarkCircle16Filled />,
  approved_with_comments: <Warning16Filled />,
  rejected: <DismissCircle16Filled />,
  cancelled: <Prohibited16Regular />,
};

const DECISION_ICON: Record<ApprovalDecisionValue, ReactElement> = {
  vso: <CheckmarkCircle16Filled />,
  vao: <Warning16Filled />,
  ref: <DismissCircle16Filled />,
};

/** The state of a flow as an icon and a text ("In approval", "Approved (VSO)"…), never colour alone. */
export function ApprovalStatusBadge({ status }: { status: ApprovalFlowStatus }) {
  const { t } = useTranslation('documents');
  return (
    <StatusChip
      intent={FLOW_STATUS_INTENT[status]}
      label={t(`approvals.status.${status}`)}
      icon={STATUS_ICON[status]}
    />
  );
}

/** A reviewer's decision: VSO, VAO or REF as an icon and a text. */
export function DecisionChip({ decision }: { decision: ApprovalDecisionValue }) {
  const { t } = useTranslation('documents');
  return (
    <StatusChip
      intent={DECISION_INTENT[decision]}
      label={t(`approvals.decisions.${decision}.chip`)}
      icon={DECISION_ICON[decision]}
    />
  );
}

/** "In approval": a flow holds the document (the files list only knows `lockedByFlowId`). */
export function InApprovalBadge() {
  return <ApprovalStatusBadge status="in_progress" />;
}
